import us from './defines';
import {Point, BoundingBox} from './lib/objects/';
import ViewportBase from './viewportBase';
import {triggerUScopeEvent} from './_uScope';
import {
  computeRotationOffset,
  limitValue,
  roundToDecimals
} from './lib/utility/helpers';
import {DragMomentum} from './lib/services/rendering/DragMomentum';
import {ViewportRotator} from './lib/services/rendering/ViewportRotator';
import {ViewportBackgroundLayer} from './lib/services/rendering/ViewportBackgroundLayer';
import {ViewportTileLayer} from './lib/services/rendering/ViewportTileLayer';
import {Renderer} from './lib/services/rendering/Renderer';
import {PaintTimer} from './lib/services/rendering/PaintTimer';
import {ViewportState} from './lib/services/rendering/ViewportState';

// ________________________________________________________________________________________________
// viewportAP.js
//    This file contains the ViewportAP (aperio viewport) object that manages the image view.
//    ViewportAP is derived from ViewportBase.
//
//    Object Name: viewportAP
//    Overview: A viewport object is associated with a viewport div.  It performs the opening/closing,
//              view presentation, navigation,and event handling for a single image and its associated markup images.
//
//    Dependencies and Supporting objects:
//        o ViewportBase
//        o cOverLayout
//
//    HTML:
//        The viewport div associated with this object is created by the uScope object.
//
//    Coordinate systems used:
//        o image base: the scanned image's pyramid base coordinate system;
//                    left,top is 0,0; positive x to the right; positive y down;
//        o viewport relative: the view coordinate system; a window into the scanned image;
//                    left,top is 0,0; positive x to the right; positive y down;

// ________________________________________________________________________________________________
//    ViewportAP - This function manages an image viewport.
//
//    Input parameters:
//        initState:
//            imageID:    req'd, image id number starting with the @ sign or filename starting with imageserver/
//            centerX:    optional, initial center of view X position
//            centerY:    optional, initial center of view X position
//            focus:        optional, initial focus (z position)
//            zoom:        optional, initial zoom where 0.5 = 200%, 1 = 100%, 2 = 50%, 4 = 25%, "Fit" and "Scan" are ok
//        uScope: a reference to the parent uScope object
export default function ViewportAP (initState, uScope) {
  // Extend base class
  ViewportBase.call(this, initState, uScope);

  // ______________________________________________________
  // Private vars
  // ______________________________________________________
  const self = this;
  let viewDragMomentum; // drag momentum object
  let jVpDiv; // Viewport div
  let vpView; // State object
  let paintTimer; // Timer for renderer
  let tileWidthXZoom;
  let tileHeightXZoom;

  // ______________________________________________________
  // IMAGE RELATED
  // ______________________________________________________
  const VPAP_IMAGE = self;
  const baseImageClose = self.imageClose;
  VPAP_IMAGE.imageClose = function () {
    baseImageClose();

    paintTimer.stop();
    self.events.trigger('viewport:image-close', self);
    self.events.$destroy();
  };

  const baseImageOpen = self.imageOpen;
  VPAP_IMAGE.imageOpen = function (inImageId) {
    const status = baseImageOpen(inImageId);
    if (status) {
      vpView.prevZoom = 1; // initialize with vpView zoom of 1 and prevZoom of fit, and let initState overrule those settings

      if (self.initState.zoom) { // the initialState zoom has priority over the initialState magnification
        self.initState.zoom = zoomConstrain(self.initState.zoom); // conferencing can initialize with an unrealizable fit zoom
        self.initState.mag = self.convertToMag(self.initState.zoom);
      } else if (self.initState.mag) { // if an initState magnification is defined, redefine zoom and view dimensions
        self.initState.zoom = 1.0; // initialize the current view zoom
        if (self.initState.mag === 'Fit') {
          self.initState.zoom = self.getFitZoom();
        } else if (self.initState.mag !== 'Scan') {
          self.initState.zoom = self.convertToZoom(self.initState.mag);
        }
      }
      setViewZoom(self.initState.zoom);
      if (self.isFitZoom(self.initState.zoom)) { // initialize the previous view zoom
        // opening at fit then the desired previous zoom is 4x mag when current zoom is Fit
        // if that prev zoom can be achieved use it,  otherwise set the prev zoom to the scan zoom
        const prevZoom = self.convertToZoom(4);
        vpView.prevZoom = (prevZoom < vpView.zoom) ? prevZoom : self.getMinZoom();
      } else { // not opening at fit, then set the previous zoom to fit
        vpView.prevZoom = self.getMaxZoom(); // constrain zoom factor to fixed zoom min and max
        vpView.zoom = zoomConstrain(self.initState.zoom); // don't use setViewZoom here - it will change prevZoom
      }
      viewDimsUpdate(); // update width, height of vpView (base image coord system)

      // initialize left, top corner of vpView (base image coord system)
      vpView.left = (self.initState.centerX) // if the initial state value is valid
        ? Math.floor(self.initState.centerX - vpView.width / 2.0) // use it
        : Math.floor((self.xinfo.get_imageWidth() - vpView.width) / 2.0); // else center the image in X

      vpView.top = (self.initState.centerY) // if the initial state value is valid
        ? Math.floor(self.initState.centerY - vpView.height / 2.0) // use it
        : Math.floor((self.xinfo.get_imageHeight() - vpView.height) / 2.0); // else center the image in Y

      viewPosInfoUpdate(vpView.left, vpView.top); // initialize the view and frame positions

      self.viewOverlayInit_(); // base class fn: load the overlay visuals

      viewDragMomentum = new DragMomentum(self); // create the drag momentum object

      if (self.initState.angle) { // .............................. now paint the first view
        self.viewRotate(self.initState.angle); // if initial rotation is set, view rotation refresh
      }

      bindEvents(); // add event handlers

      self.setInitialized(); // indicate that initialization is successful
      // Pass "true" to inform uScope that this image is in focus. Note that uScope already knows this image is
      // selected; the reason this is really done is because uScope triggers a "viewport selected" event which
      // everything and its mother listens to. This is poor control flow; uScope probably shouldn't consider
      // the image selected till it's open, and it should be the thing choosing whether the viewport is selected
      // or not. TODO: rework...
      self.setImageSelected(true);

      self.events.trigger('viewport:image-opened', self);
    }

    triggerUScopeEvent(us.evIMAGEREADY, { // fire event: the image is loaded
      imageId: self.getImageId(), uScopeIdx: self.getImageIdx(), status: self.getStatus()
    });

    return status;

    //  This function adds viewport event handlers.
    //    Note that all event handlers bound to this image use the same namespace as this viewport.
    //    So when this viewport removes its events by namespace, they are also removed.
    function bindEvents () {
      const namespace = self.eventNameSpaceGet();
      jVpDiv = self.vpDivGet();

      // TODO: Make bg image load before tiles
      self.renderer.addLayer(new ViewportRotator(self));
      self.renderer.addLayer(new ViewportBackgroundLayer(self));
      self.renderer.addLayer(new ViewportTileLayer(self));
      paintTimer.start(self.renderer.paint);

      // This event doesn't really make sense to trigger; maintaining for consistency with legacy lifecycle. TODO: Rework
      self.events.trigger('viewport:bind-events', namespace);
    }
  };

  // ______________________________________________________
  // MOOSE RELATED
  // ______________________________________________________
  const VPAP_MOUSE = self;
  const ptPrevMouseCur = new Point(0, 0); // viewport relative previous cursor position
  const ptMouseCur = new Point(0, 0); // viewport relative current cursor position
  VPAP_MOUSE.setMouseCur = function (x, y, option) {
    if (option === 'initPrev') {
      ptPrevMouseCur.set(x, y); // if initializing previous too, then previous position = new position
    } else {
      ptPrevMouseCur.setPoint(ptMouseCur); // else the previous position = current position
    }
    ptMouseCur.set(x, y); // current position = new position
  };
  VPAP_MOUSE.getMouseCur = function () {
    return ptMouseCur;
  };
  VPAP_MOUSE.getMouseCurDiff = function () { // difference between previous and current mouse positions
    return new Point(ptPrevMouseCur.x - ptMouseCur.x, ptPrevMouseCur.y - ptMouseCur.y);
  };

  // ______________________________________________________
  // VIEW NAVIGATION RELATED
  // ______________________________________________________
  const VPAP_MOVE = self;
  // This function moves the selected image the specified amount in x and y.
  //
  //    Note: This function is core to panning and so speed is very important. The vpView items are directly
  //         addressed rather than use the access functions (e.g. getViewZoom, getViewLeft, getViewTop).
  //        This is permitted as the function is a part of the cViewport object.
  VPAP_MOVE.moveRelative = function (dx, dy, options) {
    const zoom = vpView.zoom;
    const left = Math.round(vpView.left + dx * zoom); // changed to math.round from parseInt, which technically behaves more like math.floor. Just FYI in case something breaks because of it.
    const top = Math.round(vpView.top + dy * zoom);

    return self.moveAbsolute(left, top, options);
  };
  // This function moves the selected image to the specified position
  //                    and informs the state vector object of the position change.
  //
  //  RULE: Always adjust the viewport position first, then the image position wrt viewport.
  //
  //    parameters:
  //        left, top:        required, integer, the view's left,top position in base image coordinates
  //    return:                the maximum number of tileRequests - note that all, some or none may be requested (none?)
  //
  VPAP_MOVE.moveAbsolute = function (left, top, {animate} = {animate: false}) {
    if (animate) {
      enableAnimation(left, top);
    } else {
      disableAnimation();
    }

    viewPosInfoUpdate(left, top);
  };
  // This function moves the image to the specified center coords.
  VPAP_MOVE.moveAbsoluteCenter = function (centerX, centerY, animate) {
    self.moveAbsolute(
      Math.round(centerX - self.getViewWidth() / 2),
      Math.round(centerY - self.getViewHeight() / 2),
      animate);
  };
  VPAP_MOVE.viewDragMomentumGo = function () {
    if (viewDragMomentum) {
      viewDragMomentum.go();
    }
  };

  // ______________________________________________________
  // ROTATION RELATED
  // ______________________________________________________
  const VPAP_ROTATION = self;
  VPAP_ROTATION.getViewAngle = function (inDegrees) {
    return (inDegrees) ? vpView.angleDeg : vpView.angleRad;
  };
  VPAP_ROTATION.getRotationOffset = function () {
    return vpView.cpRotOffset;
  };
  VPAP_ROTATION.viewRotate = function (newAngleDeg, {animate} = { animate: false }) {
    newAngleDeg = newAngleDeg % 360; // bound input angle
    const curAngleDeg = self.getViewAngle(true); // get current angle
    if (curAngleDeg === newAngleDeg) { // and get out if there is nothing to do
      return;
    }

    if (animate && !isAnimating.transform) {
      isAnimating.transform = true;
      jVpDiv.addClass('animate');
      jVpDiv.on('transitionend', disableAnimation);
    }

    saveRotationAngle(newAngleDeg); // save the new rotation angle

    // note - the reported offset is always +/- 1 pixel from my result in computeRotationOffset

    // compute and save the rotation offset
    //  note- this offset is applied to the inner vpDiv automatically when rotated to keep centered
    const vpDivDims = self.vpDivDimsGet();
    vpView.cpRotOffset = computeRotationOffset(vpDivDims.w, vpDivDims.h, vpView.angleRad);

    function saveRotationAngle (angleDeg) {
      vpView.angleDeg = angleDeg;
      vpView.angleRad = angleDeg * us.Deg2Rad;
    }
  };

  // ______________________________________________________
  // ZOOM RELATED
  // ______________________________________________________
  const VPAP_ZOOM = self;
  let lastZoom;

  // zoomRelative, zoomAbsolute
  //    These functions implement image scaling on the specified image by the speicified zoom factor.
  //    The image center remains intact after scaling.  Scaling is constrained to min/max values
  //    and a input value of "Fit" causes a computation of the zoom factor that will fill the view
  //    with the image.  Finally the method informs the state vector object of the zoom change.
  VPAP_ZOOM.zoomRelative = function (zoomFactor) { // NOTE: only public for testing?
    if (!zoomFactor) {
      return;
    }
    const zoom = self.getViewZoom() * zoomFactor;
    return self.zoomAbsolute(zoom);
  };
  // This function refreshes the view at the commanded zoom and current view center.
  //
  //    Note that even if the new zoom is the same as the original zoom the update should still happen.
  //    This is because this occurs in the iOS case and the zoom state needs to be cleaned up via viewRefresh.
  //
  //    Passed Parameters:
  //        zoom:            required, number, the zoom value
  //                        - Note that smaller zoom result in higher resolution: .5 = 200%, 2 = 50%, 4 = 25%.
  //                        - Note that incoming zoom can be "Fit" or value of zoom at Fit
  //                            or regular value of zoom (zoom is saved as a value)
  VPAP_ZOOM.zoomAbsolute = function (zoom, center) {
    if (zoom === vpView.zoom) {
      return;
    }

    if (zoom !== 'Fit') {
      const constrainedZoom = zoomConstrain(roundToDecimals(zoom)); // constrain zoom factor to fixed zoom min and max; resolve Fit zoom
      if (constrainedZoom === lastZoom) {
        return;
      }
      zoom = constrainedZoom;
    }
    lastZoom = zoom;

    let xCenter = self.getViewCenterX(); // get viewport current center in base image pixel coords
    let yCenter = self.getViewCenterY();

    if (zoom === 'Fit' || self.isFitZoom(zoom)) {
      setViewZoom('Fit');
      const imgDims = self.getImageDims(); // in fit zoom, always center the image
      xCenter = Math.floor(imgDims.w / 2);
      yCenter = Math.floor(imgDims.h / 2);
    } else {
      setViewZoom(zoom); // cp-maybe constraint should be enforced in setViewZoom?
    }
    viewDimsUpdate(); // update vpView width and height and account for zoom

    // keep the to-be zoomed image center at the current zoom center
    vpView.left = Math.floor(xCenter - self.getViewWidth() / 2);
    vpView.top = Math.floor(yCenter - self.getViewHeight() / 2);
    if (center) {
      vpView.left += center.x;
      vpView.top += center.y;
    }

    moveConstrain(); // limit left,top so viewport always includes the image content
    viewPosInfoUpdate(vpView.left, vpView.top);
  };
  // This function steps the zoom up/down within the range and quantization of the slider.
  VPAP_ZOOM.zoomStep = function (step, center) {
    let mag = self.getViewMag();
    step = 1 + step;
    mag = constrainMag(mag * step);
    const zoom = self.convertToZoom(mag);

    self.zoomAbsolute(zoom, center);

    // This function constrains the image magnfication to the fit to allowable mag range.
    function constrainMag (requestedMag) {
      let constrainedMag = requestedMag;
      if (requestedMag > self.getMaximumMag()) {
        constrainedMag = self.getMaximumMag();
      } else if (requestedMag < self.getFitMag()) {
        constrainedMag = self.getFitMag();
      }
      return constrainedMag;
    }
  };

  // ______________________________________________________
  // VIEW RELATED
  // ______________________________________________________
  const VPAP_VIEW = self;
  VPAP_VIEW.getVPView = function () {
    return vpView;
  };
  VPAP_VIEW.getVPViewRect2 = function () {
    return new BoundingBox(vpView.left, vpView.top, vpView.left + vpView.width - 1, vpView.top + vpView.height - 1);
  };
  VPAP_VIEW.setReadOnlyMode = function (isReadOnly) {
    isReadOnly = Boolean(isReadOnly);

    if (vpView.isReadOnly !== isReadOnly) {
      vpView.isReadOnly = isReadOnly;
      self.events.trigger('state-readonly:change', isReadOnly);
    }
  };
  const baseViewResize = self.viewResize;
  VPAP_VIEW.viewResize = function (resizeThumbnail) {
    baseViewResize(resizeThumbnail);
    viewDimsUpdate();
  };
  VPAP_VIEW.getViewLeft = function () {
    return vpView.left;
  };
  VPAP_VIEW.getViewTop = function () {
    return vpView.top;
  };
  VPAP_VIEW.getViewLeftTop = function () {
    return new Point(vpView.left, vpView.top);
  };
  VPAP_VIEW.getViewCenterX = function () {
    return Math.round(vpView.left + vpView.width / 2);
  };
  VPAP_VIEW.getViewCenterY = function () {
    return Math.round(vpView.top + vpView.height / 2);
  };
  VPAP_VIEW.getViewCenter = function () {
    //  Returns view center _in base coords_
    return new Point(self.getViewCenterX(), self.getViewCenterY());
  };
  VPAP_VIEW.getViewWidth = function () {
    return vpView.width;
  };
  VPAP_VIEW.getViewHeight = function () {
    return vpView.height;
  };
  VPAP_VIEW.getViewFocus = function () {
    return vpView.focus;
  };
  // todo: reconcile prev zoom with base class?
  VPAP_VIEW.getViewPrevZoom = function () {
    // get previous zoom
    return vpView.prevZoom;
  };
  VPAP_VIEW.getViewZoom = function () {
    return vpView.zoom;
  };
  VPAP_VIEW.getViewMag = function () {
    return self.convertToMag(vpView.zoom);
  };
  VPAP_VIEW.isFitZoom = function (opt_zoom) {
    if (opt_zoom) {
      return (opt_zoom === self.getFitZoom());
    } else {
      return vpView.isFitZoom;
    }
  };

  // ______________________________________________________
  // Other stuff...
  // ______________________________________________________

  // animation tools (when viewport is moving)
  let isAnimating = {
    left: false,
    top: false,
    transform: false
  };
  const enableAnimation = (left, top) => {
    if (!isAnimating.left && left !== vpView.left) {
      isAnimating.left = true;
      jVpDiv.addClass('animate');
      jVpDiv.on('transitionend', disableAnimation);
    }
    if (!isAnimating.top && top !== vpView.top) {
      isAnimating.top = true;
      jVpDiv.addClass('animate');
      jVpDiv.on('transitionend', disableAnimation);
    }
  };
  const disableAnimation = (e) => {
    if (e) {
      isAnimating[e.originalEvent.propertyName] = false;
    } else {
      isAnimating.left = isAnimating.top = isAnimating.transform = false;
    }
    if (!isAnimating.left && !isAnimating.top && !isAnimating.transform) {
      jVpDiv.removeClass('animate');
      jVpDiv.off('transitionend');
    }
  };

  function viewPosInfoUpdate (left, top) {
    // set viewport left,top corner position
    vpView.left = left;
    vpView.top = top;

    // limit left,top so viewport always includes the image content
    moveConstrain();

    vpView.tileL = Math.floor(vpView.left / tileWidthXZoom);
    vpView.tileT = Math.floor(vpView.top / tileHeightXZoom);

    // the number of tiles contributing to a view varies as the view LT corner changes
    vpView.csTilesVisible.set(
      Math.ceil(((vpView.left - vpView.tileL * tileWidthXZoom) + vpView.width) / tileWidthXZoom),
      Math.ceil(((vpView.top - vpView.tileT * tileHeightXZoom) + vpView.height) / tileHeightXZoom)
    );

    vpView.csTiles.set(vpView.csTilesVisible.w, vpView.csTilesVisible.h);

    vpView.tileR = vpView.tileL + vpView.csTiles.w - 1;
    vpView.tileB = vpView.tileT + vpView.csTiles.h - 1;

    self.events.trigger('viewport:position-update');
  }
  function viewDimsUpdate () {
    // Note: update position must be set to compute the new view dims in tiles
    const zoom = vpView.zoom;
    const vpDivDims = self.vpDivDimsGet();
    vpView.width = Math.floor(vpDivDims.w * zoom);
    vpView.height = Math.floor(vpDivDims.h * zoom);

    if (self.isFitZoom()) { // on resize, fit zoom changes but viewPosInfoUpdate() called below needs tileWidthXZoom up to date
      tileWidthXZoom = self.getTileWidth() * zoom; // update for quick access  //todo: I'm not certain this is that useful anymore
      tileHeightXZoom = self.getTileHeight() * zoom;
    }
  }
  function setViewZoom (zoom) {
    if (self.isFitZoom() && zoom === 'Fit') { // if already at fit zoom and only updating fit zoom value
      vpView.zoom = self.getFitZoom();
    } else {
      vpView.prevZoom = vpView.zoom; // set vpView's prevZoom, zoom, and isFitZoom
      // if string passed, convert to value
      if (zoom === 'Fit') {
        zoom = self.getFitZoom();
      }
      vpView.zoom = zoom;
      vpView.isFitZoom = self.isFitZoom(zoom);
    }
    tileWidthXZoom = self.getTileWidth() * zoom; // update for quick access  // to consider: I'm not certain this is that useful anymore
    tileHeightXZoom = self.getTileHeight() * zoom;
  }
  // This function constrains the image position to remain inside the viewport.
  //    Note that for speed's sake items accessed more than once are assigned to vars.
  function moveConstrain () {
    const vpWidth2 = vpView.width / 2;
    const vpHeight2 = vpView.height / 2;

    if (vpView.left < -vpWidth2) { // moved past left edge?
      vpView.left = -vpWidth2;
    } else {
      const imgWidth = self.getImageWidth();
      if (vpView.left + vpWidth2 > imgWidth) { // moved past right edge?
        vpView.left = Math.floor(imgWidth - vpWidth2);
      }
    }

    if (vpView.top < -vpHeight2) { // moved past left top?
      vpView.top = -vpHeight2;
    } else {
      const imgHeight = self.getImageHeight();
      if (vpView.top + vpHeight2 > imgHeight) { // moved past bottom?
        vpView.top = Math.floor(imgHeight - vpHeight2);
      }
    }
  }
  // This function constrains the image zoom to reasonable values and returns the zoom value.
  //        Notes: Remember zoom: .5 = 200%, 2 = 50%, 4 = 25% and that for this call, "Fit" is a legal zoom.
  function zoomConstrain (zoom) {
    let rv = zoom;
    if (!self.isFitZoom(zoom)) { // only fit is allowed outside the zoom control fixed mag range
      const minZoom = self.getMinZoom();
      const maxZoom = self.getMaxZoom();
      rv = limitValue(zoom, maxZoom, minZoom); // note that min and max zoom values are 1/x mag, so max zoom is smaller than min
    }
    return rv;
  }

  (function init () {
    // pinch and drag momentum related
    self.initDist = 0; // pinch zoom initial distance between fingers
    self.initMag = 0; // pinch zoom initial magnfication

    // Create services
    vpView = new ViewportState((key, value) => {
      self.events.trigger('viewport:state-changed', key, value);
    });
    paintTimer = new PaintTimer(vpView);
    self.renderer = new Renderer(self);
  }());
}



// WEBPACK FOOTER //
// ./src/uScope/js/viewportAP.js